// 프론트엔드 에러 리포터 — JS/Promise/React/API 에러를 백엔드(/api/client-errors)로 전송.
// 무한루프 방지: axios(api) 가 아닌 fetch 사용(보고 요청 자체는 인터셉터 통과 안 함).
// 폭주 방지: 동일 시그니처 30초 내 중복 차단 + 분당 상한.
import { tokenStorage } from './auth'

const BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? '/api'
const APP_VERSION = (import.meta.env['VITE_APP_VERSION'] as string | undefined) ?? undefined

export type ClientErrorKind = 'js_error' | 'unhandled_rejection' | 'react' | 'api'

export interface ClientErrorPayload {
  kind: ClientErrorKind
  message: string
  stack?: string | null
  source_url?: string | null
  feature?: string | null
  http_method?: string | null
  http_path?: string | null
  http_status?: number | null
  context?: Record<string, unknown> | null
  app_version?: string | null
}

// ── throttle/dedupe ──
const _recent = new Map<string, number>() // signature → last sent ms
let _windowStart = 0
let _windowCount = 0
const _DEDUPE_MS = 30_000
const _MAX_PER_MIN = 20

function _allow(sig: string): boolean {
  const now = Date.now()
  // 분당 상한
  if (now - _windowStart > 60_000) { _windowStart = now; _windowCount = 0 }
  if (_windowCount >= _MAX_PER_MIN) return false
  // 중복 차단
  const last = _recent.get(sig)
  if (last && now - last < _DEDUPE_MS) return false
  _recent.set(sig, now)
  _windowCount++
  // 맵 비대 방지
  if (_recent.size > 200) {
    for (const [k, t] of _recent) { if (now - t > _DEDUPE_MS) _recent.delete(k) }
  }
  return true
}

// 현재 경로에서 기능명 추출 (예: /dashboard/equipment-cert → equipment-cert)
function _featureFromPath(): string | null {
  try {
    const p = window.location.pathname
    const m = p.match(/\/dashboard\/([^/]+)/) || p.match(/\/features\/([^/]+)/)
    return m ? m[1] : (p || null)
  } catch { return null }
}

export function reportClientError(p: ClientErrorPayload): void {
  try {
    const sig = `${p.kind}|${(p.message || '').slice(0, 120)}|${p.http_path || ''}`
    if (!_allow(sig)) return
    const body: ClientErrorPayload = {
      ...p,
      message: (p.message || '').slice(0, 2000),
      stack: p.stack ? String(p.stack).slice(0, 8000) : null,
      source_url: p.source_url ?? (typeof window !== 'undefined' ? window.location.href : null),
      feature: p.feature ?? _featureFromPath(),
    }
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const tok = tokenStorage.getAccess()
    if (tok) headers['Authorization'] = `Bearer ${tok}`
    if (APP_VERSION) body.app_version = APP_VERSION
    // keepalive: 페이지 이탈 중에도 전송. 실패는 무시(에러 리포터가 에러 내면 안 됨).
    void fetch(`${BASE_URL}/client-errors`, {
      method: 'POST', headers, body: JSON.stringify(body), keepalive: true,
    }).catch(() => {})
  } catch { /* 리포터 자체 오류는 삼킴 */ }
}

// 전역 핸들러 설치 (main.tsx 에서 1회 호출)
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('error', (e: ErrorEvent) => {
    // 리소스 로드 에러(img 등)는 message 없음 → 건너뜀
    if (!e.message) return
    reportClientError({
      kind: 'js_error',
      message: e.message,
      stack: e.error?.stack ?? `${e.filename}:${e.lineno}:${e.colno}`,
    })
  })
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason
    const msg = reason instanceof Error ? reason.message : String(reason)
    // axios 에러는 api 인터셉터에서 별도 보고하므로 여기선 제외(중복 방지)
    if (reason && typeof reason === 'object' && 'config' in reason && 'isAxiosError' in reason) return
    reportClientError({
      kind: 'unhandled_rejection',
      message: msg || 'unhandledrejection',
      stack: reason instanceof Error ? reason.stack : null,
    })
  })
}
